import axios from 'axios';
import { toast } from 'react-toastify';
import { translations, defaultLanguage, getTranslation } from '../locales';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const getPreferredLanguage = () => {
  const saved = localStorage.getItem('preferredLanguage');
  if (saved && translations[saved]) return saved;
  return defaultLanguage;
};

const t = (key, fallback) => {
  const lang = getPreferredLanguage();
  const dict = translations[lang] || translations[defaultLanguage];
  return getTranslation(dict, key, fallback);
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and language
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const language = localStorage.getItem('preferredLanguage') || 'fr';
    config.headers['Accept-Language'] = language;

    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let refreshQueue = [];

const processRefreshQueue = (error, token = null) => {
  refreshQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  refreshQueue = [];
};

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response } = error;
    const originalRequest = error.config;
    const suppressErrorToast = Boolean(originalRequest?.suppressErrorToast);

    if (response?.status === 401 && !originalRequest?._retry) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken
        });

        const { token, refresh_token: newRefreshToken } = refreshResponse.data || {};
        if (token) {
          localStorage.setItem('token', token);
        }
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }
        processRefreshQueue(null, token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processRefreshQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (response?.status === 403) {
      if (suppressErrorToast) {
        return Promise.reject(error);
      }
      if (response?.data?.message) {
        toast.error(response.data.message);
      } else {
        toast.error(t('messages.errors.unauthorized'));
      }
    } else if (response?.status >= 500) {
      if (suppressErrorToast) {
        return Promise.reject(error);
      }
      toast.error(t('messages.errors.serverError'));
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  refresh: (data) => api.post('/auth/refresh', data),
  logout: (data) => api.post('/auth/logout', data),
  getIndustries: (language) => api.get('/auth/industries', { params: { language } }),
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  resendVerification: (email, language) => api.post('/auth/resend-verification', { email, language }),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// Offers API
export const offersAPI = {
  getAll: (params) => api.get('/offers', { params }),
  getById: (id) => api.get(`/offers/${id}`),
  create: (data) => api.post('/offers', data),
  update: (id, data) => api.put(`/offers/${id}`, data),
  delete: (id) => api.delete(`/offers/${id}`),
  publish: (id) => api.post(`/offers/${id}/publish`),
  unpublish: (id) => api.post(`/offers/${id}/unpublish`),
  getStats: () => api.get('/offers/stats/overview'),
};

// CVs API
export const cvsAPI = {
  getAll: (params) => api.get('/cv', { params }),
  getById: (id) => api.get(`/cv/${id}`),
  upload: (formData) => api.post('/cv/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  extractText: (id) => api.post(`/cv/${id}/extract-text`),
  delete: (id) => api.delete(`/cv/${id}`),
  getStats: () => api.get('/cv/stats/overview'),
  getPublishedOffers: () => api.get('/cv/published-offers'),
  checkDependencies: (id) => api.get(`/cv/${id}/check-dependencies`),
  getFile: (id, disposition = 'attachment') => api.get(`/cv/${id}/file`, {
    params: { disposition },
    responseType: 'blob'
  }),
  getPreviewHtml: (id) => api.get(`/cv/${id}/preview-html`)
};

// Applications API
export const applicationsAPI = {
  getAll: (params) => api.get('/applications', { params, suppressErrorToast: true }),
  getById: (id) => api.get(`/applications/${id}`, { suppressErrorToast: true }),
  updateStatus: (id, data) => api.patch(`/applications/${id}/status`, data, { suppressErrorToast: true }),
  getStatsByOffer: () => api.get('/applications/stats/by-offer', { suppressErrorToast: true }),
  delete: (id, data) => api.delete(`/applications/${id}`, { data, suppressErrorToast: true }),
  checkAppointments: (id) => api.get(`/applications/${id}/check-appointments`, { suppressErrorToast: true }),
  addToKanban: (id) => api.post(`/applications/${id}/add-to-kanban`, {}, { suppressErrorToast: true }),
  getNotes: (id, params) => api.get(`/applications/${id}/notes`, { params, suppressErrorToast: true }),
  createNote: (id, data) => api.post(`/applications/${id}/notes`, data, { suppressErrorToast: true }),
  updateNote: (id, noteId, data) => api.put(`/applications/${id}/notes/${noteId}`, data, { suppressErrorToast: true }),
  deleteNote: (id, noteId) => api.delete(`/applications/${id}/notes/${noteId}`, { suppressErrorToast: true })
};

// Analysis API
export const analysisAPI = {
  analyze: (data) => api.post('/analyze', data),
  analyzeAsync: (data) => api.post('/analyze/async', data),
  getJobStatus: (jobId) => api.get(`/analyze/jobs/${jobId}`),
  getUnanalyzed: (offerId) => api.get(`/analyze/unanalyzed/${offerId}`),
  getHistory: (offerId) => api.get(`/analyze/history/${offerId}`)
};

// Results API
export const resultsAPI = {
  getByOffer: (offerId, params) => api.get(`/results/${offerId}`, { params, suppressErrorToast: true }),
  getDetail: (resultId) => api.get(`/results/detail/${resultId}`, { suppressErrorToast: true }),
  exportCsv: (offerId) => api.get(`/results/export/csv/${offerId}`, {
    responseType: 'blob',
    suppressErrorToast: true
  }),
  delete: (resultId) => api.delete(`/results/${resultId}`, { suppressErrorToast: true }),
};

// Settings API
export const settingsAPI = {
  getMe: () => api.get('/settings/me'),
  updateProfile: (data) => api.put('/settings/me', data),
  getPreferences: () => api.get('/settings/preferences', { suppressErrorToast: true }),
  updatePreferences: (data) => api.put('/settings/preferences', data, { suppressErrorToast: true }),
  getCompany: () => api.get('/settings/company'),
  updateCompany: (data) => api.put('/settings/company', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAdminLanguage: (workspaceId) => api.get(`/settings/admin-language/${workspaceId}`),
  getCleanup: () => api.get('/settings/cleanup'),
  updateCleanup: (data) => api.put('/settings/cleanup', data),
  getMinioConfig: () => api.get('/settings/minio-config'),
  testMinioConfig: (data) => api.post('/settings/minio-config/test', data),
  saveMinioConfig: (data) => api.post('/settings/minio-config', data),
  toggleMinioConfig: (data) => api.put('/settings/minio-config/toggle', data),
  deleteMinioConfig: () => api.delete('/settings/minio-config'),
  estimateMinioMigration: () => api.get('/settings/minio-migration/estimate'),
  migrateToCustomMinio: () => api.post('/settings/minio-migration/to-custom'),
  migrateFromCustomMinio: () => api.post('/settings/minio-migration/from-custom'),
  getAiProviders: () => api.get('/settings/ai-providers'),
  updateAiProvider: (provider, data) => api.put(`/settings/ai-providers/${provider}`, data),
  deleteAiProvider: (provider) => api.delete(`/settings/ai-providers/${provider}`),
  getJobs: (params) => api.get('/settings/jobs', { params }),
  getJob: (jobId) => api.get(`/settings/jobs/${jobId}`),
  getNotifications: () => api.get('/settings/notifications'),
  updateNotifications: (data) => api.put('/settings/notifications', data),
};

// Admin API
export const adminAPI = {
  getUsers: (params) => api.get('/admin/users', { params }),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  resendVerification: (id) => api.post(`/admin/users/${id}/resend-verification`),
  getDashboard: () => api.get('/admin/dashboard'),
};

// Super Admin API
export const superAdminAPI = {
  getEnvVars: () => api.get('/superadmin/env'),
  getEnvVar: (key) => api.get(`/superadmin/env/${key}`),
  createEnvVar: (data) => api.post('/superadmin/env', data),
  updateEnvVar: (key, data) => api.put(`/superadmin/env/${key}`, data),
  deleteEnvVar: (key) => api.delete(`/superadmin/env/${key}`),
  getAdmins: (params) => api.get('/superadmin/admins', { params }),
  getDashboard: () => api.get('/superadmin/dashboard')
};

// Notes API
export const notesAPI = {
  getAll: (params) => api.get('/notes', { params, suppressErrorToast: true }),
  getRecent: (params) => api.get('/notes/recent', { params, suppressErrorToast: true }),
  create: (data) => api.post('/notes', data, { suppressErrorToast: true }),
  update: (id, data) => api.put(`/notes/${id}`, data, { suppressErrorToast: true }),
  delete: (id) => api.delete(`/notes/${id}`, { suppressErrorToast: true }),
  getShares: (id) => api.get(`/notes/${id}/shares`, { suppressErrorToast: true }),
  share: (id, data) => api.post(`/notes/${id}/share`, data, { suppressErrorToast: true }),
  unshare: (id, data) => api.delete(`/notes/${id}/share`, { data, suppressErrorToast: true }),
  getTeamMembers: () => api.get('/notes/team-members', { suppressErrorToast: true })
};

// Candidates API
export const candidatesAPI = {
  getAll: (params, options = {}) => api.get('/candidates', { params, ...options }),
  createManual: (data, options = {}) => api.post('/candidates/manual', data, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Content-Type': 'multipart/form-data'
    }
  }),
  update: (id, data, options = {}) => api.put(`/candidates/${id}`, data, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Content-Type': 'multipart/form-data'
    }
  }),
  shareToKanban: (id, options = {}) => api.post(`/candidates/${id}/share-to-kanban`, {}, options),
  unshareFromKanban: (id, options = {}) => api.post(`/candidates/${id}/unshare-from-kanban`, {}, options),
  aiAnalyze: (data, options = {}) => api.post('/candidates/ai-analyze', data, options),
  delete: (id, options = {}) => api.delete(`/candidates/${id}`, options),
  move: (id, data, options = {}) => api.put(`/candidates/${id}/move`, data, options)
};

// Kanban API
export const kanbanAPI = {
  getBoard: (params, options = {}) => api.get('/kanban/board', { params, ...options }),
  createColumn: (data, options = {}) => api.post('/kanban/columns', data, options),
  updateColumn: (id, data, options = {}) => api.put(`/kanban/columns/${id}`, data, options),
  deleteColumn: (id, options = {}) => api.delete(`/kanban/columns/${id}`, options),
  reorderColumns: (data, options = {}) => api.put('/kanban/columns/reorder', data, options),
  reorderCards: (data, options = {}) => api.put('/kanban/cards/reorder', data, options)
};

// Activities API
export const activitiesAPI = {
  getActivities: (params) => api.get('/activities', { params })
};

// Analytics API
export const analyticsAPI = {
  getOffersMetrics: (params) => api.get('/analytics/offers', { params }),
  getCandidatesMetrics: (params) => api.get('/analytics/candidates', { params }),
  getQualityMetrics: (params) => api.get('/analytics/quality', { params }),
  getAiMetrics: (params) => api.get('/analytics/ai', { params }),
  getActivityStats: (params) => api.get('/analytics/activity-stats', { params }),
  getPreferences: () => api.get('/analytics/preferences', { suppressErrorToast: true }),
  updatePreferences: (data) => api.post('/analytics/preferences', data, { suppressErrorToast: true })
};

// Appointments API
export const appointmentsAPI = {
  getAppointments: (params) => api.get('/appointments', { params }),
  getCalendarAppointments: (params) => api.get('/appointments/calendar', { params }),
  getSharedAppointments: (params) => api.get('/appointments/shared', { params }),
  getRecentInvitations: (params) => api.get('/appointments/recent-invitations', { params }),
  checkStatus: (email) => api.get('/appointments/check-status', { params: { email } }),
  getAvailabilities: () => api.get('/appointments/availability', { suppressErrorToast: true }),
  createAvailability: (data) => api.post('/appointments/availability', data, { suppressErrorToast: true }),
  updateAvailability: (id, data) => api.put(`/appointments/availability/${id}`, data, { suppressErrorToast: true }),
  deleteAvailability: (id) => api.delete(`/appointments/availability/${id}`, { suppressErrorToast: true }),
  getHolidays: () => api.get('/appointments/holidays', { suppressErrorToast: true }),
  createHoliday: (data) => api.post('/appointments/holidays', data, { suppressErrorToast: true }),
  updateHoliday: (id, data) => api.put(`/appointments/holidays/${id}`, data, { suppressErrorToast: true }),
  deleteHoliday: (id) => api.delete(`/appointments/holidays/${id}`, { suppressErrorToast: true }),
  getBookingSettings: () => api.get('/appointments/booking-settings'),
  saveBookingSettings: (data) => api.post('/appointments/booking-settings', data),
  generateBookingSlug: () => api.post('/appointments/booking-settings/generate-slug'),
  checkBookingSlug: (slug) => api.get(`/appointments/booking-settings/check-slug/${slug}`),
  getVideoConferenceSettings: () => api.get('/appointments/video-conference'),
  saveVideoConferenceSettings: (data) => api.post('/appointments/video-conference', data),
  deleteVideoConferenceSettings: () => api.delete('/appointments/video-conference'),
  testVideoMeeting: (data) => api.post('/appointments/video-conference/test-meeting', data),
  generateVideoMeeting: (data) => api.post('/appointments/video-conference/generate-meeting', data),
  getInvitationContract: (params) => api.get('/appointments/send-invitation/contract', { params, suppressErrorToast: true }),
  sendInvitation: (data) => api.post('/appointments/send-invitation', data, { suppressErrorToast: true }),
  getGoogleAuth: () => api.get('/appointments/google-auth'),
  exchangeGoogleToken: (code) => api.post('/appointments/google-token', { code }),
  getPublicBooking: (slug) => api.get(`/public/booking/${slug}`),
  getPublicSlots: (slug, data) => api.post(`/public/booking/${slug}/slots`, data, { suppressErrorToast: true }),
  bookPublic: (slug, data) => api.post(`/public/booking/${slug}/book`, data, { suppressErrorToast: true }),
  confirmAppointment: (id, data) => api.put(`/appointments/${id}/confirm`, data),
  cancelAppointment: (id, data) => api.put(`/appointments/${id}/cancel`, data),
  updateAppointment: (id, data) => api.put(`/appointments/${id}`, data, { suppressErrorToast: true }),
  rescheduleAppointment: (id, data) => api.put(`/appointments/${id}/reschedule`, data, { suppressErrorToast: true })
};

// Jobs API
export const jobsAPI = {
  getCleanupJobs: () => api.get('/jobs/cleanup'),
  runCleanupManual: (data) => api.post('/jobs/cleanup/manual', data)
};

// Contact API
export const contactAPI = {
  send: (data) => api.post('/contact', data),
  sendAuthenticated: (data) => api.post('/contact/authenticated', data),
  getInfo: () => api.get('/contact/info')
};

// Community API
export const communityAPI = {
  joinWaitlist: (data) => api.post('/community/waitlist', data),
  getWaitlist: (params) => api.get('/superadmin/community-waitlist', { params, suppressErrorToast: true })
};

// Navigation API
export const navigationAPI = {
  track: (data) => api.post('/navigation/track', data, { suppressErrorToast: true }),
  getQuickActions: (params) => api.get('/navigation/quick-actions', { params, suppressErrorToast: true }),
  getStats: (params) => api.get('/navigation/stats', { params, suppressErrorToast: true })
};

// Helper functions
export const handleApiError = (error, defaultMessage = t('messages.errors.generic')) => {
  console.error('API Error:', error);

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.response?.data?.errors) {
    return error.response.data.errors.map(err => err.msg || err.message).join(', ');
  }
  if (error?.response?.status === 500) {
    return t('messages.errors.serverError');
  }
  if (error?.response?.status === 403) {
    return t('messages.errors.unauthorized');
  }
  if (error?.response?.status === 401) {
    return t('messages.errors.sessionExpired');
  }
  if (error?.message) {
    return error.message;
  }
  return defaultMessage;
};

export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default api;
